import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Building2, CreditCard, FileText, History, ChevronRight, Phone, Mail, IndianRupee, AlertCircle, CheckCircle, Clock, Filter, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import BankAccountSelector from '../components/BankAccountSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuppliersPage() {
  const [activeTab, setActiveTab] = useState('suppliers');
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showLedger, setShowLedger] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch suppliers
  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/suppliers`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSuppliers(data);
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  }, []);

  // Fetch purchases
  const fetchPurchases = useCallback(async () => {
    try {
      let url = `${API_URL}/api/suppliers/purchases`;
      if (filterStatus !== 'all') {
        url += `?payment_status=${filterStatus}`;
      }
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setPurchases(data);
      }
    } catch (err) {
      console.error('Error fetching purchases:', err);
    }
  }, [filterStatus]);

  // Fetch payments
  const fetchPayments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/suppliers/payments`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPayments(data);
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
    }
  }, []);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/suppliers/reports/summary`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSuppliers(), fetchPurchases(), fetchPayments(), fetchSummary()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSuppliers, fetchPurchases, fetchPayments, fetchSummary]);

  useEffect(() => {
    fetchPurchases();
  }, [filterStatus, fetchPurchases]);

  // Fetch supplier ledger
  const fetchLedger = async (supplierId) => {
    try {
      const res = await fetch(`${API_URL}/api/suppliers/${supplierId}/ledger`, { headers });
      if (res.ok) {
        const data = await res.json();
        setLedgerData(data);
        setShowLedger(true);
      }
    } catch (err) {
      toast.error('Failed to load ledger');
    }
  };

  // Filter suppliers by search
  const filteredSuppliers = suppliers.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.phone?.includes(searchTerm)
  );

  // Supplier Form Component
  const SupplierForm = ({ supplier, onClose, onSave }) => {
    const [formData, setFormData] = useState(supplier || {
      name: '', contact_person: '', phone: '', email: '', address: '',
      gst_number: '', pan_number: '', bank_name: '', bank_account: '', bank_ifsc: '',
      category: '', notes: ''
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.name) {
        toast.error('Supplier name is required');
        return;
      }
      setSaving(true);
      try {
        const url = supplier ? `${API_URL}/api/suppliers/${supplier.id}` : `${API_URL}/api/suppliers`;
        const method = supplier ? 'PUT' : 'POST';
        const res = await fetch(url, { method, headers, body: JSON.stringify(formData) });
        if (res.ok) {
          toast.success(supplier ? 'Supplier updated' : 'Supplier created');
          onSave();
          onClose();
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Failed to save');
        }
      } catch (err) {
        toast.error('Error saving supplier');
      }
      setSaving(false);
    };

    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{supplier ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Supplier Name *</label>
                <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Enter supplier name" />
              </div>
              <div>
                <label className="text-sm font-medium">Contact Person</label>
                <Input value={formData.contact_person} onChange={e => setFormData({ ...formData, contact_person: e.target.value })} placeholder="Contact person name" />
              </div>
              <div>
                <label className="text-sm font-medium">Phone</label>
                <Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="Phone number" />
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="Email address" type="email" />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Address</label>
                <Input value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} placeholder="Full address" />
              </div>
              <div>
                <label className="text-sm font-medium">GST Number</label>
                <Input value={formData.gst_number} onChange={e => setFormData({ ...formData, gst_number: e.target.value })} placeholder="GST number" />
              </div>
              <div>
                <label className="text-sm font-medium">PAN Number</label>
                <Input value={formData.pan_number} onChange={e => setFormData({ ...formData, pan_number: e.target.value })} placeholder="PAN number" />
              </div>
              <div>
                <label className="text-sm font-medium">Category</label>
                <Select value={formData.category} onValueChange={v => setFormData({ ...formData, category: v })}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stationery">Stationery</SelectItem>
                    <SelectItem value="books">Books</SelectItem>
                    <SelectItem value="uniform">Uniform</SelectItem>
                    <SelectItem value="electronics">Electronics</SelectItem>
                    <SelectItem value="furniture">Furniture</SelectItem>
                    <SelectItem value="sports">Sports Equipment</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Bank Details</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">Bank Name</label>
                  <Input value={formData.bank_name} onChange={e => setFormData({ ...formData, bank_name: e.target.value })} placeholder="Bank name" />
                </div>
                <div>
                  <label className="text-sm font-medium">Account Number</label>
                  <Input value={formData.bank_account} onChange={e => setFormData({ ...formData, bank_account: e.target.value })} placeholder="Account number" />
                </div>
                <div>
                  <label className="text-sm font-medium">IFSC Code</label>
                  <Input value={formData.bank_ifsc} onChange={e => setFormData({ ...formData, bank_ifsc: e.target.value })} placeholder="IFSC code" />
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Input value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Supplier'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  // Purchase Form Component
  const PurchaseForm = ({ onClose, onSave }) => {
    const [formData, setFormData] = useState({
      supplier_id: '', bill_number: '', bill_date: new Date().toISOString().split('T')[0],
      due_date: '', items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
      subtotal: 0, tax_amount: 0, discount: 0, total_amount: 0, notes: ''
    });
    const [saving, setSaving] = useState(false);

    const updateItem = (index, field, value) => {
      const newItems = [...formData.items];
      newItems[index][field] = value;
      if (field === 'quantity' || field === 'rate') {
        newItems[index].amount = newItems[index].quantity * newItems[index].rate;
      }
      const subtotal = newItems.reduce((sum, item) => sum + item.amount, 0);
      const total = subtotal + formData.tax_amount - formData.discount;
      setFormData({ ...formData, items: newItems, subtotal, total_amount: total });
    };

    const addItem = () => {
      setFormData({ ...formData, items: [...formData.items, { description: '', quantity: 1, rate: 0, amount: 0 }] });
    };

    const removeItem = (index) => {
      if (formData.items.length > 1) {
        const newItems = formData.items.filter((_, i) => i !== index);
        const subtotal = newItems.reduce((sum, item) => sum + item.amount, 0);
        const total = subtotal + formData.tax_amount - formData.discount;
        setFormData({ ...formData, items: newItems, subtotal, total_amount: total });
      }
    };

    const updateTotals = (field, value) => {
      const numValue = parseFloat(value) || 0;
      const newData = { ...formData, [field]: numValue };
      newData.total_amount = newData.subtotal + newData.tax_amount - newData.discount;
      setFormData(newData);
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.supplier_id || !formData.bill_number) {
        toast.error('Supplier and Bill Number are required');
        return;
      }
      setSaving(true);
      try {
        const res = await fetch(`${API_URL}/api/suppliers/purchases`, { method: 'POST', headers, body: JSON.stringify(formData) });
        if (res.ok) {
          toast.success('Purchase entry created');
          onSave();
          onClose();
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Failed to create purchase');
        }
      } catch (err) {
        toast.error('Error creating purchase');
      }
      setSaving(false);
    };

    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Purchase / Bill Entry</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Supplier *</label>
                <Select value={formData.supplier_id} onValueChange={v => setFormData({ ...formData, supplier_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Bill Number *</label>
                <Input value={formData.bill_number} onChange={e => setFormData({ ...formData, bill_number: e.target.value })} placeholder="Invoice/Bill number" />
              </div>
              <div>
                <label className="text-sm font-medium">Bill Date</label>
                <Input type="date" value={formData.bill_date} onChange={e => setFormData({ ...formData, bill_date: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium">Due Date</label>
                <Input type="date" value={formData.due_date} onChange={e => setFormData({ ...formData, due_date: e.target.value })} />
              </div>
            </div>

            <div className="border rounded-lg p-4">
              <h4 className="font-medium mb-3">Items</h4>
              <div className="space-y-2">
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-5">
                      <Input value={item.description} onChange={e => updateItem(index, 'description', e.target.value)} placeholder="Description" />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" value={item.quantity} onChange={e => updateItem(index, 'quantity', parseInt(e.target.value) || 0)} placeholder="Qty" />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" value={item.rate} onChange={e => updateItem(index, 'rate', parseFloat(e.target.value) || 0)} placeholder="Rate" />
                    </div>
                    <div className="col-span-2 text-right font-medium">₹{item.amount.toLocaleString()}</div>
                    <div className="col-span-1">
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)} className="text-red-500">×</Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addItem} className="mt-2">+ Add Item</Button>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium">Subtotal</label>
                <Input value={formData.subtotal.toLocaleString()} disabled className="bg-gray-50" />
              </div>
              <div>
                <label className="text-sm font-medium">Tax Amount</label>
                <Input type="number" value={formData.tax_amount} onChange={e => updateTotals('tax_amount', e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium">Discount</label>
                <Input type="number" value={formData.discount} onChange={e => updateTotals('discount', e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium">Total Amount</label>
                <Input value={`₹${formData.total_amount.toLocaleString()}`} disabled className="bg-gray-50 font-bold" />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Notes</label>
              <Input value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Purchase'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  // Payment Form Component
  const PaymentForm = ({ supplier, onClose, onSave }) => {
    const [formData, setFormData] = useState({
      supplier_id: supplier?.id || '', amount: '', payment_method: 'bank_transfer',
      bank_account_id: '', payment_reference: '', payment_date: new Date().toISOString().split('T')[0], notes: ''
    });
    const [saving, setSaving] = useState(false);
    const [supplierPurchases, setSupplierPurchases] = useState([]);

    useEffect(() => {
      if (formData.supplier_id) {
        fetch(`${API_URL}/api/suppliers/purchases?supplier_id=${formData.supplier_id}&payment_status=pending`, { headers })
          .then(res => res.json())
          .then(data => setSupplierPurchases(data.filter(p => p.payment_status !== 'paid')))
          .catch(() => {});
      }
    }, [formData.supplier_id]);

    const selectedSupplierData = suppliers.find(s => s.id === formData.supplier_id);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.supplier_id || !formData.amount) {
        toast.error('Supplier and Amount are required');
        return;
      }
      setSaving(true);
      try {
        const payload = { 
          ...formData, 
          amount: parseFloat(formData.amount),
          bank_account_id: formData.payment_method !== 'cash' ? formData.bank_account_id : null
        };
        const res = await fetch(`${API_URL}/api/suppliers/payments`, { method: 'POST', headers, body: JSON.stringify(payload) });
        if (res.ok) {
          toast.success('Payment recorded successfully');
          onSave();
          onClose();
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Failed to record payment');
        }
      } catch (err) {
        toast.error('Error recording payment');
      }
      setSaving(false);
    };

    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Payment to Supplier</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Supplier *</label>
              <Select value={formData.supplier_id} onValueChange={v => setFormData({ ...formData, supplier_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>
                  {suppliers.filter(s => s.balance_due > 0).map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} (Due: ₹{s.balance_due?.toLocaleString()})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedSupplierData && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Total Purchases:</span>
                  <span className="font-medium">₹{selectedSupplierData.total_purchases?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Total Paid:</span>
                  <span className="font-medium">₹{selectedSupplierData.total_paid?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm font-bold text-red-600">
                  <span>Balance Due:</span>
                  <span>₹{selectedSupplierData.balance_due?.toLocaleString()}</span>
                </div>
              </div>
            )}

            {supplierPurchases.length > 0 && (
              <div className="text-sm text-gray-600">
                <p className="font-medium mb-1">Pending Bills:</p>
                {supplierPurchases.slice(0, 3).map(p => (
                  <div key={p.id} className="flex justify-between">
                    <span>{p.bill_number} ({p.bill_date})</span>
                    <span>₹{p.balance?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Amount *</label>
                <Input type="number" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} placeholder="Payment amount" />
              </div>
              <div>
                <label className="text-sm font-medium">Payment Date</label>
                <Input type="date" value={formData.payment_date} onChange={e => setFormData({ ...formData, payment_date: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Payment Method</label>
                <Select value={formData.payment_method} onValueChange={v => setFormData({ ...formData, payment_method: v, bank_account_id: '' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Reference (Cheque/UTR)</label>
                <Input value={formData.payment_reference} onChange={e => setFormData({ ...formData, payment_reference: e.target.value })} placeholder="Reference number" />
              </div>
            </div>

            {/* Bank Account Selection */}
            <div>
              <label className="text-sm font-medium">
                {formData.payment_method === 'cash' ? 'Cash Account' : 'Bank Account *'}
              </label>
              <BankAccountSelector
                value={formData.bank_account_id}
                onChange={(id) => setFormData({ ...formData, bank_account_id: id })}
                paymentMethod={formData.payment_method}
                showBalance={true}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Notes</label>
              <Input value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Payment notes" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Processing...' : 'Record Payment'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  // Ledger Dialog
  const LedgerDialog = () => (
    <Dialog open={showLedger} onOpenChange={() => setShowLedger(false)}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Supplier Ledger - {ledgerData?.supplier?.name}</DialogTitle>
        </DialogHeader>
        {ledgerData && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-gray-500">Total Purchases</p>
                  <p className="text-xl font-bold">₹{ledgerData.summary.total_purchases?.toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-gray-500">Total Payments</p>
                  <p className="text-xl font-bold text-green-600">₹{ledgerData.summary.total_payments?.toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-gray-500">Balance Due</p>
                  <p className="text-xl font-bold text-red-600">₹{ledgerData.summary.balance_due?.toLocaleString()}</p>
                </CardContent>
              </Card>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left">Date</th>
                    <th className="p-3 text-left">Reference</th>
                    <th className="p-3 text-left">Description</th>
                    <th className="p-3 text-right">Debit</th>
                    <th className="p-3 text-right">Credit</th>
                    <th className="p-3 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerData.ledger.map((entry, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-3">{entry.date}</td>
                      <td className="p-3">{entry.reference}</td>
                      <td className="p-3">{entry.description}</td>
                      <td className="p-3 text-right">{entry.debit > 0 ? `₹${entry.debit.toLocaleString()}` : '-'}</td>
                      <td className="p-3 text-right text-green-600">{entry.credit > 0 ? `₹${entry.credit.toLocaleString()}` : '-'}</td>
                      <td className="p-3 text-right font-medium">₹{entry.balance?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Suppliers Management</h1>
          <p className="text-gray-500">Manage suppliers, purchases, and payments</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowPaymentForm(true)} variant="outline" data-testid="record-payment-btn">
            <CreditCard className="w-4 h-4 mr-2" /> Record Payment
          </Button>
          <Button onClick={() => setShowPurchaseForm(true)} variant="outline" data-testid="new-purchase-btn">
            <FileText className="w-4 h-4 mr-2" /> New Purchase
          </Button>
          <Button onClick={() => { setSelectedSupplier(null); setShowSupplierForm(true); }} data-testid="add-supplier-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Supplier
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg"><Building2 className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="text-sm text-gray-500">Total Suppliers</p>
                  <p className="text-xl font-bold">{summary.total_suppliers}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg"><FileText className="w-5 h-5 text-purple-600" /></div>
                <div>
                  <p className="text-sm text-gray-500">Total Purchases</p>
                  <p className="text-xl font-bold">₹{summary.total_purchases?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg"><CheckCircle className="w-5 h-5 text-green-600" /></div>
                <div>
                  <p className="text-sm text-gray-500">Total Paid</p>
                  <p className="text-xl font-bold text-green-600">₹{summary.total_paid?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg"><AlertCircle className="w-5 h-5 text-red-600" /></div>
                <div>
                  <p className="text-sm text-gray-500">Balance Due</p>
                  <p className="text-xl font-bold text-red-600">₹{summary.total_balance_due?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="purchases">Purchases/Bills</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
        </TabsList>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle>All Suppliers</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Search suppliers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredSuppliers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No suppliers found. Add your first supplier.</div>
                ) : (
                  filteredSuppliers.map(supplier => (
                    <div key={supplier.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50" data-testid={`supplier-${supplier.id}`}>
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium">{supplier.name}</h3>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            {supplier.contact_person && <span>{supplier.contact_person}</span>}
                            {supplier.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{supplier.phone}</span>}
                            {supplier.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{supplier.email}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Balance Due</p>
                          <p className={`font-bold ${supplier.balance_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            ₹{supplier.balance_due?.toLocaleString() || 0}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => fetchLedger(supplier.id)} title="View Ledger">
                            <History className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedSupplier(supplier); setShowSupplierForm(true); }} title="Edit">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Purchases Tab */}
        <TabsContent value="purchases" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle>Purchases / Bills</CardTitle>
                <div className="flex gap-2">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-3 text-left">Bill No</th>
                      <th className="p-3 text-left">Supplier</th>
                      <th className="p-3 text-left">Bill Date</th>
                      <th className="p-3 text-left">Due Date</th>
                      <th className="p-3 text-right">Amount</th>
                      <th className="p-3 text-right">Paid</th>
                      <th className="p-3 text-right">Balance</th>
                      <th className="p-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.length === 0 ? (
                      <tr><td colSpan={8} className="p-8 text-center text-gray-500">No purchases found</td></tr>
                    ) : (
                      purchases.map(p => (
                        <tr key={p.id} className="border-t hover:bg-gray-50">
                          <td className="p-3 font-medium">{p.bill_number}</td>
                          <td className="p-3">{p.supplier_name}</td>
                          <td className="p-3">{p.bill_date}</td>
                          <td className="p-3">{p.due_date}</td>
                          <td className="p-3 text-right">₹{p.total_amount?.toLocaleString()}</td>
                          <td className="p-3 text-right text-green-600">₹{p.amount_paid?.toLocaleString()}</td>
                          <td className="p-3 text-right text-red-600">₹{p.balance?.toLocaleString()}</td>
                          <td className="p-3 text-center">
                            <Badge variant={p.payment_status === 'paid' ? 'success' : p.payment_status === 'partial' ? 'warning' : 'destructive'}>
                              {p.payment_status}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Payment History</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-3 text-left">Payment Ref</th>
                      <th className="p-3 text-left">Supplier</th>
                      <th className="p-3 text-left">Date</th>
                      <th className="p-3 text-left">Method</th>
                      <th className="p-3 text-left">Reference</th>
                      <th className="p-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.length === 0 ? (
                      <tr><td colSpan={6} className="p-8 text-center text-gray-500">No payments found</td></tr>
                    ) : (
                      payments.map(p => (
                        <tr key={p.id} className="border-t hover:bg-gray-50">
                          <td className="p-3 font-medium">{p.payment_ref_no}</td>
                          <td className="p-3">{p.supplier_name}</td>
                          <td className="p-3">{p.payment_date}</td>
                          <td className="p-3 capitalize">{p.payment_method?.replace('_', ' ')}</td>
                          <td className="p-3">{p.payment_reference || '-'}</td>
                          <td className="p-3 text-right font-medium text-green-600">₹{p.amount?.toLocaleString()}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {showSupplierForm && <SupplierForm supplier={selectedSupplier} onClose={() => setShowSupplierForm(false)} onSave={() => { fetchSuppliers(); fetchSummary(); }} />}
      {showPurchaseForm && <PurchaseForm onClose={() => setShowPurchaseForm(false)} onSave={() => { fetchPurchases(); fetchSuppliers(); fetchSummary(); }} />}
      {showPaymentForm && <PaymentForm supplier={selectedSupplier} onClose={() => setShowPaymentForm(false)} onSave={() => { fetchPayments(); fetchSuppliers(); fetchPurchases(); fetchSummary(); }} />}
      <LedgerDialog />
    </div>
  );
}
